import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Trophy } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import {
  cancelFixtureSiblings,
  generateTournamentFixture,
  getFixtureSiblings,
  updateTournament,
} from '@/api/tournament/tournamentApi';
import { handleGlobalError } from '@/utils/error-handler';
import { logError } from '@/utils/logger';
import { HEXAGONAL_TEAM_COUNT } from '@/utils/tournament-format';
import { FieldHelp } from '@/features/guided-help';
import {
  AmericanFinalBracketSize,
  SiblingCancellationStatus,
  TournamentFormat,
  type FixtureSibling,
  type FixtureSiblingCancellation,
  type Tournament,
} from '@/types/tournament.model';
import { AmericanFinalBracketSizeField } from '@/features/tournament-wizard/blocks/fixture-config';

const CONFIRMATION_PHRASE = 'Crear';

type SiblingsState =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; siblings: FixtureSibling[] };

const names = (siblings: FixtureSibling[]) =>
  siblings.map((s) => s.name).join(', ');

/**
 * The promised siblings that still show up as pending once the fixture is created. The backend
 * skips a sibling whose generation fails (it only logs it), while a generated one leaves the list,
 * so these are the ones that failed. Null when the list can't be fetched again.
 */
async function siblingsStillPending(
  tournamentId: string,
  promised: FixtureSibling[],
): Promise<FixtureSibling[] | null> {
  if (promised.length === 0) return [];
  try {
    const pendingIds = new Set(
      (await getFixtureSiblings(tournamentId)).map((s) => s.id),
    );
    return promised.filter((s) => pendingIds.has(s.id));
  } catch (error) {
    logError('CreateFixtureModal: siblings recheck', error);
    return null;
  }
}

/**
 * What the success toast says about the siblings. `promised` is null when the list never loaded,
 * and `pending` when it could not be checked again after creating.
 */
function siblingsOutcome(
  promised: FixtureSibling[] | null,
  pending: FixtureSibling[] | null,
  sharedScope: string,
): string {
  if (promised === null) {
    return ` Si había torneos listos que comparten ${sharedScope}, también se generaron los suyos: revisá que cada uno tenga su fixture.`;
  }
  if (promised.length === 0) return '';
  if (pending === null) {
    return ` También se intentó generar el fixture de: ${names(promised)}. Revisá que cada uno lo tenga.`;
  }
  const generated = promised.filter((s) => !pending.includes(s));
  return [
    generated.length > 0 && ` También se generó el fixture de: ${names(generated)}.`,
    pending.length > 0 &&
      ` No se pudo generar el de: ${names(pending)}; crealo desde ese torneo para ver el motivo.`,
  ]
    .filter(Boolean)
    .join('');
}

/**
 * What the toast (or the notice, if the fixture then fails) says about the siblings that were
 * asked to cancel. Names come from `shown`, the list the modal displayed. `anyFailed` is true when
 * a cancellation raised an error, which the organizer has to look at.
 */
function cancellationOutcome(
  shown: FixtureSibling[],
  results: FixtureSiblingCancellation[],
): { text: string; anyFailed: boolean } {
  const nameOf = (id: string) => shown.find((s) => s.id === id)?.name ?? id;
  const cancelled = results
    .filter((r) => r.status === SiblingCancellationStatus.CANCELLED)
    .map((r) => nameOf(r.id));
  const notCancelled = results
    .filter((r) => r.status !== SiblingCancellationStatus.CANCELLED)
    .map((r) => (r.reason ? `${nameOf(r.id)} (${r.reason})` : nameOf(r.id)));
  const text = [
    cancelled.length > 0 && ` Se cancelaron: ${cancelled.join(', ')}.`,
    notCancelled.length > 0 && ` No se canceló: ${notCancelled.join(', ')}.`,
  ]
    .filter(Boolean)
    .join('');
  return {
    text,
    anyFailed: results.some((r) => r.status === SiblingCancellationStatus.FAILED),
  };
}

interface CreateFixtureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tournamentId: string;
  tournament: Tournament;
  onCreated: () => void;
}

export function CreateFixtureModal({
  open,
  onOpenChange,
  tournamentId,
  tournament,
  onCreated,
}: CreateFixtureModalProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  // What was cancelled when the fixture then failed to generate; stays until the modal closes.
  const [cancelNotice, setCancelNotice] = useState<string | null>(null);
  // Bumped to fetch the siblings list again after a failed generation.
  const [listVersion, setListVersion] = useState(0);
  const [siblingsState, setSiblingsState] = useState<SiblingsState>({
    status: 'loading',
  });
  const [bracketSize, setBracketSize] = useState<AmericanFinalBracketSize>(
    tournament.americanFinalBracketSize ?? AmericanFinalBracketSize.NONE,
  );

  const isAmerican = tournament.format === TournamentFormat.AMERICAN;
  // LONG siblings share a weekly grid, and the list also brings the ones that are not generated
  // with this one, each with its reason.
  const isLong = tournament.format === TournamentFormat.LONG;
  const sharedScope = isLong ? 'la grilla semanal' : 'el bloque de horarios';
  const teamCount = tournament.teams?.length ?? 0;
  // With more than 5 teams multiple groups are formed → hide "No final bracket"
  // except with exactly 6, which is the Hexagonal (a single group of 6)
  const formsMultipleGroups = teamCount > 5;
  const isHexagonalCount = teamCount === HEXAGONAL_TEAM_COUNT;
  const hideNone = formsMultipleGroups && !isHexagonalCount;
  // With 3+ groups (9+ teams) FINAL would generate a bracket of 4 with a BYE instead of a real final
  const hideFinal = teamCount > 8;
  // With fewer than 8 teams a bracket of 8 is not possible
  const hideEight = teamCount < 8;

  // Reset bracket size when modal opens
  useEffect(() => {
    if (open) {
      const stored = tournament.americanFinalBracketSize ?? AmericanFinalBracketSize.NONE;
      // If the stored option became hidden, fall back to a valid visible one.
      // FOUR is never hidden, so it's the safe fallback.
      let effective = stored;
      // With 6 teams NONE is visible (Hexagonal) but not the default: open on "Solo final".
      if (stored === AmericanFinalBracketSize.NONE && formsMultipleGroups) {
        effective = hideFinal ? AmericanFinalBracketSize.FOUR : AmericanFinalBracketSize.FINAL;
      } else if (stored === AmericanFinalBracketSize.FINAL && hideFinal) {
        effective = AmericanFinalBracketSize.FOUR;
      } else if (stored === AmericanFinalBracketSize.EIGHT && hideEight) {
        effective = AmericanFinalBracketSize.FOUR;
      }
      setBracketSize(effective);
    }
  }, [open, tournament.americanFinalBracketSize, formsMultipleGroups, hideFinal, hideEight]);

  useEffect(() => {
    if (!open) {
      setConfirmationText('');
      setCancelNotice(null);
      return;
    }

    let cancelled = false;
    setSiblingsState({ status: 'loading' });

    // LONG neither cancels nor gets cancelled, so only the other formats ask for the ones to cancel.
    getFixtureSiblings(tournamentId, {
      includeNotGenerated: isLong,
      includeToCancel: !isLong,
    })
      .then((siblings) => {
        if (!cancelled) setSiblingsState({ status: 'ready', siblings });
      })
      .catch((error) => {
        // The list is informative only: creating the fixture does not depend on it.
        logError('CreateFixtureModal: siblings', error);
        if (!cancelled) setSiblingsState({ status: 'failed' });
      });

    return () => {
      cancelled = true;
    };
  }, [open, tournamentId, isLong, listVersion]);

  const listedSiblings =
    siblingsState.status === 'ready' ? siblingsState.siblings : [];
  const siblingsToCancel = listedSiblings.filter((s) => s.toCancel);
  const sharingSiblings = listedSiblings.filter((s) => !s.toCancel);
  const generatedSiblings = sharingSiblings.filter((s) => !s.notGeneratedReason);
  const hasSiblings = generatedSiblings.length > 0;
  // Until the list says otherwise (still loading, or the request failed) the backend may well
  // generate siblings, so the warning stays: only a loaded, empty list drops it.
  const mayHaveSiblings = siblingsState.status !== 'ready' || hasSiblings;

  const isConfirmed = confirmationText === CONFIRMATION_PHRASE;

  const handleCreateFixture = async () => {
    if (!isConfirmed) return;
    setIsCreating(true);
    try {
      // For AMERICAN format, persist the bracket size before anything else: it can fail, and the
      // cancellations below cannot be undone.
      if (isAmerican) {
        try {
          await updateTournament(tournamentId, { americanFinalBracketSize: bracketSize });
        } catch (error) {
          logError('CreateFixtureModal: bracket size', error);
          handleGlobalError(
            error,
            'Error',
            'No se pudo crear el fixture. Inténtalo de nuevo.',
          );
          return;
        }
      }

      // Only what the modal showed gets cancelled; with the list down or still loading,
      // siblingsToCancel is empty and nothing is.
      let cancellations: FixtureSiblingCancellation[] = [];
      if (siblingsToCancel.length > 0) {
        try {
          cancellations = await cancelFixtureSiblings(
            tournamentId,
            siblingsToCancel.map((s) => s.id),
          );
        } catch (error) {
          logError('CreateFixtureModal: cancel siblings', error);
          handleGlobalError(
            error,
            'Error',
            'No se pudo confirmar la cancelación de los torneos que no llegaron al mínimo, así que no se creó el fixture. Revisá la lista e intentá de nuevo.',
          );
          // The request may have failed after the server cancelled some (a timeout, a dropped
          // connection): fetch the list again so it shows what is still left to cancel.
          setListVersion((v) => v + 1);
          return;
        }
      }
      const cancellation = cancellationOutcome(siblingsToCancel, cancellations);

      try {
        await generateTournamentFixture(tournamentId);

        const promised =
          siblingsState.status === 'ready' ? generatedSiblings : null;
        const pending = promised
          ? await siblingsStillPending(tournamentId, promised)
          : null;
        toast({
          title: 'Fixture creado',
          description: `El fixture se creó en modo borrador.${siblingsOutcome(promised, pending, sharedScope)}${cancellation.text}`,
          // A sibling that failed is left without a fixture, and one that failed to cancel is still
          // open: the organizer has to act on either.
          variant:
            (pending && pending.length > 0) || cancellation.anyFailed
              ? 'destructive'
              : 'default',
        });

        onOpenChange(false);
        onCreated();
      } catch (error) {
        logError('CreateFixtureModal: create', error);
        handleGlobalError(
          error,
          'Error',
          'No se pudo crear el fixture. Inténtalo de nuevo.',
        );
        // The toast above is the only one that fits (TOAST_LIMIT = 1), so what was already cancelled
        // goes in the modal, and the list is fetched again so a retry does not offer those again.
        if (
          cancellations.some(
            (c) => c.status === SiblingCancellationStatus.CANCELLED,
          )
        ) {
          setCancelNotice(`El fixture no se creó.${cancellation.text}`);
          setListVersion((v) => v + 1);
        }
      }
    } finally {
      setIsCreating(false);
    }
  };

  return (
    // Esc, the X and an outside click all arrive here: ignore them while creating, so the dialog is
    // still open when a failed generation sets the cancel notice. The component's own
    // onOpenChange(false) calls (after a successful create) do not go through this guard.
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isCreating) onOpenChange(next);
      }}
    >
      <DialogContent className="flex max-h-[90dvh] flex-col overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-blue-600" />
            Crear fixture
          </DialogTitle>
          <DialogDescription>
            Estás por crear el fixture de este torneo.
            {hasSiblings
              ? ` También se genera el fixture de: ${names(generatedSiblings)}.`
              : mayHaveSiblings &&
                ` Si hay torneos que comparten ${sharedScope} y están listos para generar el suyo, también se generan.`}
            {siblingsToCancel.length > 0 &&
              ` Antes se cancela: ${names(siblingsToCancel)}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {isAmerican && (
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="flex items-center gap-2 font-medium text-slate-900">
                  Cuadro final
                  <FieldHelp topicId="wizard.fixture.americanFinalBracket" />
                </span>
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                  {teamCount} equipo{teamCount !== 1 ? 's' : ''} inscripto{teamCount !== 1 ? 's' : ''}
                </span>
              </div>
              <AmericanFinalBracketSizeField
                value={bracketSize}
                onChange={setBracketSize}
                disabled={isCreating}
                hideNone={hideNone}
                hideFinal={hideFinal}
                hideEight={hideEight}
                hideCard
                noneAsHexagonal={isHexagonalCount}
              />
            </div>
          )}

          <div className="rounded-lg bg-blue-50 p-4 text-sm text-blue-900">
            <p className="font-medium">Antes de continuar:</p>
            <ul className="mt-2 list-inside list-disc space-y-1">
              <li>
                El fixture se creará en modo{' '}
                <span className="font-semibold uppercase">borrador</span> y solo
                vos podrás verlo como manager.
              </li>
              <li>
                Una vez creado, las inscripciones quedarán cerradas por
                completo.
              </li>
              {mayHaveSiblings && (
                <li>
                  {hasSiblings
                    ? 'Las inscripciones de los torneos que se generan junto con este también se cierran.'
                    : 'Si se genera otro torneo junto con este, su inscripción también se cierra.'}
                </li>
              )}
              {siblingsToCancel.length > 0 && (
                <li>
                  Los torneos que no llegaron al mínimo se cancelan antes de
                  crear el fixture.
                </li>
              )}
            </ul>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            <p className="font-medium text-slate-900">
              {isLong
                ? 'Torneos que comparten la grilla semanal'
                : 'Torneos que se generan junto con este'}
            </p>
            {siblingsState.status === 'loading' ? (
              <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                <Loader2 className="h-3 w-3 animate-spin" />
                Cargando torneos…
              </div>
            ) : siblingsState.status === 'failed' ? (
              <p className="mt-1 text-xs text-slate-600">
                No pudimos cargar la lista. Podés crear el fixture igual; no se
                cancela ningún torneo.
              </p>
            ) : sharingSiblings.length === 0 ? (
              <p className="mt-1 text-xs text-slate-600">
                {isLong
                  ? 'Este torneo no comparte su grilla semanal con otros torneos.'
                  : 'No se genera ningún otro torneo junto con este.'}
              </p>
            ) : (
              <ul className="mt-2 list-inside list-disc space-y-0.5 text-xs text-slate-700">
                {sharingSiblings.map((s) => (
                  <li key={s.id}>
                    {s.name}
                    {isLong && (
                      <span className="text-slate-500">
                        {' — '}
                        {s.notGeneratedReason
                          ? `no se genera: ${s.notGeneratedReason}`
                          : 'se genera también'}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {siblingsToCancel.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900">
              <p className="font-medium">Torneos que se van a cancelar</p>
              <ul className="mt-2 list-inside list-disc space-y-0.5 text-xs">
                {siblingsToCancel.map((s) => (
                  <li key={s.id}>
                    {s.name}
                    <span className="text-red-700">
                      {` — ${s.teamsRegistered ?? 0} de ${s.minTeams ?? 0} parejas`}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs">
                No llegaron al mínimo de parejas. Se cancelan antes de crear el
                fixture: se devuelve todo lo pagado, efectivo incluido, sin
                penalidad, y a las parejas inscriptas les llega el aviso.
              </p>
            </div>
          )}

          {cancelNotice && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              {cancelNotice}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="create-fixture-confirmation">
              Para confirmar, escribí{' '}
              <span className="font-semibold">{CONFIRMATION_PHRASE}</span>
            </Label>
            <Input
              id="create-fixture-confirmation"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder={CONFIRMATION_PHRASE}
              autoComplete="off"
              disabled={isCreating}
            />
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isCreating}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCreateFixture}
            disabled={!isConfirmed || isCreating}
          >
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creando...
              </>
            ) : (
              'Crear fixture'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
